import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Users, Clock, MessageCircle, RefreshCw, LogOut, ArrowLeft, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, detailText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LanguageToggle from "@/components/app/LanguageToggle";
import PasswordInput from "@/components/app/PasswordInput";

const ADMIN_WA_NUMBER = process.env.REACT_APP_ADMIN_WA || "6281703200075";

export default function Register() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref"); // wedding_id from a partner's invite link, if any
  const [form, setForm] = useState({ name: "", phone: "", password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [done, setDone] = useState(false);

  // Cek status: coba login otomatis; jika disetujui lanjut ke workspace,
  // jika masih pending lempar ke halaman menunggu persetujuan (/activate).
  const checkStatus = async () => {
    setChecking(true);
    try {
      const res = await api.post("/auth/login", { phone: form.phone.trim(), password: form.password });
      const data = await refresh();
      if (!data?.user) {
        toast.error("Gagal memeriksa status akun. Coba lagi.");
        return;
      }
      if (data.membership) {
        toast.success("Akun Anda sudah aktif!");
      } else {
        toast.info("Akun Anda masih menunggu persetujuan admin.");
      }
      navigate(res.data?.user?.must_change_password ? "/first-login" : "/dashboard");
    } catch {
      toast.error("Nomor HP atau password salah");
    } finally {
      setChecking(false);
    }
  };

  const contactAdminViaWA = () => {
    const message = `Halo Wedd, saya ingin Memverifikasi akun Weddly Wedding Planner. Akun saya: ${form.phone}`;
    window.open(`https://wa.me/${ADMIN_WA_NUMBER}?text=${encodeURIComponent(message)}`, "_blank", "noopener,noreferrer");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Nama wajib diisi");
    if (!form.phone.trim()) return toast.error("Nomor HP wajib diisi");
    if (form.password.length < 6) return toast.error("Password minimal 6 karakter");
    if (form.password !== form.confirm) return toast.error("Konfirmasi password tidak cocok");

    setLoading(true);
    try {
      await api.post("/auth/register", {
        name: form.name.trim(),
        phone: form.phone.trim(),
        password: form.password,
        ref: ref || null,
      });
      setDone(true);
    } catch (err) {
      toast.error(detailText(err?.response?.data?.detail) || "Pendaftaran gagal");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#FFFCF9] text-[#35282C] p-6 relative selection:bg-[#F9EFF0] selection:text-[#9E4053]">
        <div className="max-w-md w-full text-center bg-white p-8 sm:p-10 rounded-3xl border border-[#D3B68C]/30 shadow-luxury">
          <img
            src="/weddly-logo.png"
            alt="Logo Weddly"
            className="h-16 w-auto object-contain mx-auto mb-3"
          />
          <div className="flex flex-col items-center leading-tight mb-4">
            <span className="font-serif-title text-2xl font-bold text-[#35282C]">
              Wedding Planner
            </span>
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#B89768]">
              BY WEDDLY
            </span>
          </div>

          <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-3.5 py-1 text-xs font-semibold mb-4">
            <Clock className="h-3.5 w-3.5 text-amber-600" /> Menunggu Persetujuan Admin
          </div>

          <h2 className="font-serif-title text-2xl font-bold text-[#35282C] mb-2">Akun Menunggu Aktivasi</h2>
          <p className="text-xs sm:text-sm text-[#90777D] mb-6 leading-relaxed">
            Akun dengan nomor HP{" "}
            <strong className="font-semibold text-[#35282C]">{form.phone}</strong>{" "}
            sudah kami terima dan sedang menunggu verifikasi admin. Selesaikan verifikasi via WhatsApp, lalu muat ulang setelah akun Anda aktif.
          </p>

          <div className="space-y-3">
            <Button
              onClick={contactAdminViaWA}
              className="w-full h-11 rounded-full bg-[#BE5366] hover:bg-[#9E4053] text-white text-xs font-semibold shadow-md gap-2"
            >
              <MessageCircle className="h-4 w-4" /> Verifikasi Admin via WhatsApp
            </Button>
            <Button
              onClick={checkStatus}
              disabled={checking}
              variant="outline"
              className="w-full h-11 rounded-full text-xs font-semibold border-[#35282C]/20 hover:bg-[#F9EFF0]"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${checking ? "animate-spin" : ""}`} />
              {checking ? "Memeriksa..." : "Sudah Diaktifkan? Muat Ulang"}
            </Button>
          </div>
        </div>

        <footer className="fixed bottom-0 left-0 right-0 pb-6 text-center">
          <Button
            onClick={() => navigate("/login")}
            variant="ghost"
            size="sm"
            className="rounded-full text-xs text-[#90777D] hover:text-[#BE5366]"
          >
            <LogOut className="mr-2 h-4 w-4" /> Kembali ke Login
          </Button>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full grid md:grid-cols-2 bg-[#FFFCF9] text-[#35282C] relative selection:bg-[#F9EFF0] selection:text-[#9E4053]">
      {/* Navigation Top Left */}
      <div className="absolute top-4 left-4 z-30 md:top-6 md:left-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#90777D] hover:text-[#BE5366] bg-white/80 backdrop-blur-md px-3.5 py-2 rounded-full border border-[#D3B68C]/30 shadow-sm transition-all"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          <span>Kembali ke Beranda</span>
        </Link>
      </div>

      {/* Language Switcher Top Right */}
      <div className="absolute top-4 right-4 z-30 md:top-6 md:right-6">
        <LanguageToggle />
      </div>

      {/* Left Column Editorial Banner (Desktop) */}
      <div className="relative hidden md:block overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1519741497674-611481863552?w=1600&q=80"
          alt="Weddly Registration Editorial"
          className="absolute inset-0 h-full w-full object-cover filter brightness-[0.7] contrast-[1.05]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#35282C]/90 via-[#35282C]/50 to-[#9E4053]/30" />

        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img
              src="/weddly-logo.png"
              alt="Weddly Logo"
              className="h-12 w-auto object-contain brightness-125 shrink-0"
            />
            <div className="flex flex-col leading-tight">
              <span className="font-serif-title text-xl font-bold tracking-tight text-white">
                Wedding Planner
              </span>
              <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-[#D3B68C]">
                BY WEDDLY
              </span>
            </div>
          </div>

          <div className="space-y-4">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#D3B68C] block">
              ✦ Mulai Langkah Pertamamu
            </span>
            <h1 className="font-serif-title text-4xl lg:text-5xl leading-tight font-bold">
              Your Dream Wedding Starts Here.
            </h1>
            <p className="text-[#F9EFF0]/85 text-sm leading-relaxed max-w-md font-light">
              Mulai perjalanan menuju hari bahagiamu dengan persiapan yang lebih mudah, rapi, dan menyenangkan bersama pasangan tercinta.
            </p>

            <div className="pt-2 space-y-2.5 text-xs text-[#F9EFF0]">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#D3B68C]" />
                <span>Mulai uji coba gratis 14 hari tanpa kartu kredit</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#D3B68C]" />
                <span>Kelola 12 modul pernikahan lengkap dalam satu tempat</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-[#D3B68C]" />
                <span>Sinkronisasi real-time antara Anda & pasangan</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Column Form Container */}
      <div className="flex items-center justify-center p-6 sm:p-12 md:p-16 bg-[#FFFCF9]">
        <form onSubmit={submit} className="w-full max-w-md space-y-5 pt-12 md:pt-0">
          {/* Mobile Logo Branding */}
          <div className="flex items-center gap-3 md:hidden mb-2">
            <img
              src="/weddly-logo.png"
              alt="Weddly Logo"
              className="h-11 w-auto object-contain"
            />
            <div className="flex flex-col leading-tight">
              <span className="font-serif-title text-lg font-bold text-[#35282C]">
                Wedding Planner
              </span>
              <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-[#B89768]">
                BY WEDDLY
              </span>
            </div>
          </div>

          <div>
            <h2 className="font-serif-title text-3xl sm:text-4xl font-bold text-[#35282C]">
              Daftar Akun Baru
            </h2>
            <p className="text-sm text-[#90777D] mt-1">
              Lengkapi data di bawah untuk membuat workspace pernikahan Anda.
            </p>
          </div>

          {ref && (
            <div className="flex items-start gap-2.5 p-3.5 rounded-xl bg-[#F9EFF0] border border-[#D3B68C]/30 text-xs text-[#9E4053] leading-relaxed">
              <Users className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                Anda mendaftar melalui undangan pasangan. Akun Anda akan otomatis terhubung ke workspace pernikahan yang sama.
              </span>
            </div>
          )}

          <div className="space-y-3.5">
            <div>
              <Label className="text-xs font-bold text-[#35282C]">Nama Lengkap *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="misal: Amelia Permata"
                className="mt-1 border-[#F1E5E7] text-xs h-10 bg-white"
              />
            </div>

            <div>
              <Label className="text-xs font-bold text-[#35282C]">Nomor HP *</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="8123456789"
                inputMode="tel"
                className="mt-1 border-[#F1E5E7] text-xs h-10 bg-white"
              />
              <p className="text-[11px] text-[#90777D] mt-1">Nomor Indonesia, tanpa awalan 0. Contoh: 8123456789</p>
            </div>

            <div>
              <Label className="text-xs font-bold text-[#35282C]">Password *</Label>
              <PasswordInput
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="register-password-input"
              />
            </div>

            <div>
              <Label className="text-xs font-bold text-[#35282C]">Konfirmasi Password *</Label>
              <PasswordInput
                value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                data-testid="register-password-confirm-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11 rounded-full text-sm font-semibold text-white bg-[#BE5366] hover:bg-[#9E4053] shadow-md hover:shadow-floating transition-all duration-200 mt-4"
          >
            {loading ? "Memproses..." : "Daftar Akun Weddly"}
          </Button>

          <p className="text-center text-xs text-[#90777D]">
            Sudah punya akun?{" "}
            <Link to="/login" className="text-[#BE5366] font-bold hover:underline">
              Masuk
            </Link>
          </p>

          <div className="pt-3 border-t border-[#F1E5E7] text-xs text-[#90777D] text-center">
            <div className="flex items-center justify-center gap-1.5 text-[11px]">
              <Sparkles className="h-3.5 w-3.5 text-[#BE5366]" />
              <span>Satu lisensi = satu workspace terpadu bersama pasangan</span>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
